import React, { useContext, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridOptions } from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';
import { DialogBox, Loader, APIAlert } from '@mummssoftware/common-ui';
import { HopeGlobalContext } from '@mummssoftware/global-context';
import moment from 'moment-timezone';
import { CLOSE_HIS_MODAL, SET_GRID_API, ADM, DIS } from '../Context/constants';
import { Context } from '../../context';
import {
  MultiPatientTableWrap,
  StyledButton,
} from '../../components/layout/WrapperDiv';
import { MultiPatientCellRenderer } from './MultiPatientCellRenderer';
import { HospiceItemExpandedTable } from '../HospiceItemSet/HospiceItemExpandedTable';
import { HISContext } from '../Context/HISContext';
import ColumnHeader from './ColumnHeader';
import {
  Approval,
  Cmsapproved,
  Download,
  typeMap,
  Due,
  Submit_Due,
} from '../HospiceItemSet/constants';
import defaults from '../../translations/en.json';

export const MultiPatientTable = () => {
  const {
    props: {
      grid: {
        rowHeight,
        className,
        largeColumnWidth = 190,
        smallColumnWidth = 110,
      },
    },
    palette,
  } = useTheme() as any;

  const {
    alertInfo,
    individualPatientHeader,
    individualPatientHIS,
    modalOpen,
    modalTitle,
    dispatch,
    approvePatientHIS,
    clearAlert,
    gridAPI,
    userCanApprove,
  } = useContext(HISContext);
  const { onWidgetReady } = useContext(Context);

  const { globalCrossPatientHIS } = useContext(HopeGlobalContext);

  const frameworkComponents = {
    MultiPatientCellRenderer,
    ButtonHeader: ColumnHeader,
  };

  useEffect(() => {
    if (globalCrossPatientHIS) {
      // callback to alert dashboard the view is ready
      onWidgetReady && onWidgetReady('HospiceItemSet' as mumms.ComponentName);
      console.log('## HOSPICE ITEM SET WIDGET END ##', new Date());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCrossPatientHIS]);

  const closeHISModal = () => {
    dispatch({ type: CLOSE_HIS_MODAL, payload: null });
  };

  // Get the display value to the data value
  const actionValueGetter = ({ data: { type } }: any): string =>
    typeMap.get(type)[0] || type;

  // Determines which date to display from the dto
  // const dateValueGetter = ({ data, data: { type } }: any): string =>
  //   data[dateMap.get(type) || ''];

  // Determines which due date and time till
  const dateValueGetter = ({
    colDef: { field },
    data,
    data: { type },
  }: any): string => {
    // get the admit or discharge date based on row.
    const selectedDate = data[typeMap.get(type)[2] || ''];
    let returnDate = selectedDate;
    switch (field) {
      case Due:
        returnDate = getApprovalDate(selectedDate, type);
        break;
      case Submit_Due:
        returnDate = moment(selectedDate).add(30, 'd').format('MM/DD/YYYY');
        break;
      default:
    }
    return returnDate;
  };

  // Get the approvalDate for based off business rules in OP-31471
  const getApprovalDate = (selectedDate: string, type: string) => {
    let returnDate = '';
    if (!selectedDate || !type || ![ADM, DIS].includes(type)) {
      return returnDate;
    }
    if (type === ADM) {
      returnDate = moment(selectedDate).add(14, 'd').format('MM/DD/YYYY');
    } else if (type === DIS) {
      returnDate = moment(selectedDate).add(7, 'd').format('MM/DD/YYYY');
    }
    return returnDate;
  };

  // exclude rows which need approval, adds expanded prop to data, re-draws
  const expandRow = (event: any) => {
    const { api, node } = event;
    const isCollapsed = node.rowHeight === rowHeight;
    node.setRowHeight((isCollapsed && 90) || rowHeight);
    api.onRowHeightChanged();
    if (node.data.expanded === undefined) {
      node.data.expanded = true;
    } else {
      node.data.expanded = !node.data.expanded;
    }
    const rowNodes = [api.getDisplayedRowAtIndex(node.rowIndex)];
    api.redrawRows({ rowNodes });
  };

  const columnDefs: any = [
    {
      headerName: defaults.PatientNumber,
      field: 'patientnumber',
      width: smallColumnWidth,
      onCellClicked: expandRow,
    },
    {
      headerName: defaults.LastName,
      field: 'patientlastname',
      width: smallColumnWidth,
      onCellClicked: expandRow,
    },
    {
      headerName: defaults.FirstName,
      field: 'patientfirstname',
      width: smallColumnWidth,
      onCellClicked: expandRow,
    },
    {
      headerName: defaults.Type,
      field: 'type',
      valueGetter: actionValueGetter,
      width: smallColumnWidth,
      onCellClicked: expandRow,
    },
    {
      headerName: defaults.Date,
      field: 'date',
      valueGetter: dateValueGetter,
      width: smallColumnWidth,
      onCellClicked: expandRow,
    },
    {
      headerName: defaults.Due,
      field: Due,
      valueGetter: dateValueGetter,
      width: largeColumnWidth,
      cellRendererFramework: MultiPatientCellRenderer,
      cellRendererParams: {
        title: Due,
        expandRow,
      },
    },
    {
      headerName: defaults.SubmissionDue,
      field: Submit_Due,
      valueGetter: dateValueGetter,
      width: largeColumnWidth,
      cellRendererFramework: MultiPatientCellRenderer,
      cellRendererParams: {
        title: Submit_Due,
        expandRow,
      },
    },

    {
      headerName: defaults.Approval,
      field: 'approvaleventdisplay',
      width: largeColumnWidth,
      cellRendererFramework: MultiPatientCellRenderer,
      cellRendererParams: {
        title: Approval,
        expandRow,
      },
    },
    {
      headerName: defaults.submissionToCMS,
      field: 'downloadeventdisplay',
      width: largeColumnWidth,
      minWidth: largeColumnWidth,
      headerComponent: 'ButtonHeader',
      cellRendererFramework: MultiPatientCellRenderer,
      cellRendererParams: {
        title: Download,
        expandRow,
      },
    },
    {
      headerName: defaults.CMSAcceptance,
      field: 'accepteventdisplay',
      width: largeColumnWidth,
      cellRendererFramework: MultiPatientCellRenderer,
      cellRendererParams: {
        title: Cmsapproved,
        expandRow,
      },
    },
  ];

  const gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      suppressMenu: true,
    },
    rowHeight,
    headerHeight: 40,
    animateRows: true,
    columnDefs,
    onGridReady: (params: any) => {
      if (!gridAPI) {
        dispatch({
          type: SET_GRID_API,
          payload: params.api,
        });
      }
    },
  };
  return (
    <MultiPatientTableWrap
      // id='hospice-info-table-wrapper'
      className={`${className} light`}
    >
      {alertInfo && (
        // TODO refactor to parent component (currently pure)
        <APIAlert errorInfo={alertInfo} handleClose={clearAlert} />
      )}
      <DialogBox
        title={modalTitle as string}
        disableBackdropClick
        open={modalOpen}
        handleClick={closeHISModal}
        fullWidth
        maxWidth="lg"
        disableScrollLock
        minHeight
        style={{ marginLeft: '2VW' }}
        TopButton={
          userCanApprove && (
            <StyledButton
              variant="contained"
              style={{
                backgroundColor: palette.primary.main,
                color: palette.text.contrastPrimaryPaper,
              }}
              onClick={approvePatientHIS}
              disabled={!individualPatientHeader || !individualPatientHIS}
            >
              {defaults.Approve}
            </StyledButton>
          )
        }
      >
        <React.Fragment>
          {individualPatientHeader && individualPatientHIS && (
            <HospiceItemExpandedTable multiPatient />
          )}
          {(!individualPatientHeader || !individualPatientHIS) && <Loader />}
        </React.Fragment>
      </DialogBox>
      <AgGridReact
        gridOptions={gridOptions}
        rowData={globalCrossPatientHIS}
        frameworkComponents={frameworkComponents}
        animateRows
        suppressRowClickSelection
        suppressScrollOnNewData
      />
    </MultiPatientTableWrap>
  );
};
